import React, { useCallback, useEffect, useRef, useState } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { extractSeqIdNumber } from "../../helpers/utils";
import DiscoverService from "../../services/discoverService";

// Overrides MUI's default tooltip max-width (~300px) so a 60-residue sequence
// line (~71 monospace chars including the position prefix) fits on one line.
const useTooltipStyles = makeStyles({
    tooltip: {
        maxWidth: 620,
    },
});


function parseAlignmentSequences(rawAlignment) {
    if (!rawAlignment) return null;

    let query = '';
    let subject = '';
    String(rawAlignment).split('\n').forEach(line => {
        const qMatch = line.match(/^\s*Q:\s*\d+\s+(\S.*?)\s+\d+\s*$/);
        if (qMatch) {
            query += qMatch[1].replace(/-/g, '');
            return;
        }
        const sMatch = line.match(/^\s*S:\s*\d+\s+(\S.*?)\s+\d+\s*$/);
        if (sMatch) {
            subject += sMatch[1].replace(/-/g, '');
        }
    });

    return (query || subject) ? { query, subject } : null;
}

function findDocForSeqId(data, patentNumber, seqIdNo) {
    if (!data?.docItems || !patentNumber || !seqIdNo) return null;
    return Object.values(data.docItems).find(doc =>
        doc && doc.SUBJECT_PN === patentNumber && String(extractSeqIdNumber(doc)) === String(seqIdNo)
    ) || null;
}

// Parses the "do=gqfetch&template=sequence" numbered-block HTML (e.g.
// "  1   MAEIIRKQLW YPGYKPENLS ... <br />") into a single plain residue string.
function parseSequenceListingHtml(html) {
    if (!html) return null;
    const text = String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
    let seq = '';
    text.split('\n').forEach(line => {
        const match = line.match(/^\s*\d+\s+(.*\S)\s*$/);
        if (match) {
            seq += match[1].replace(/\s+/g, '');
        }
    });
    return seq || null;
}

// Renders a plain residue string as numbered blocks of 10, matching standard
// sequence listing format, e.g.:
//   1   DKKYSIGLDI GTNSVGWAVI TDEYKVPSKK
//  31   FKVLGNTDRH SIKKNLIGAL
function formatSequenceBlocks(seq, residuesPerLine = 60, blockSize = 10) {
    if (!seq) return '';
    const totalLines = Math.ceil(seq.length / residuesPerLine);
    const numWidth = String((totalLines - 1) * residuesPerLine + 1).length;
    const lines = [];
    for (let i = 0; i < seq.length; i += residuesPerLine) {
        const chunk = seq.slice(i, i + residuesPerLine);
        const blocks = [];
        for (let j = 0; j < chunk.length; j += blockSize) {
            blocks.push(chunk.slice(j, j + blockSize));
        }
        lines.push(`${String(i + 1).padStart(numWidth)}   ${blocks.join(' ')}`);
    }
    return lines.join('\n');
}

const CLOSE_DELAY_MS = 200;
const COPY_FEEDBACK_MS = 1500;
const SEQ_PREVIEW_LENGTH = 250;


const _rawSeqCache = new Map();

function fetchCacheKey(patentNumber, seqIdNo) {
    return `${patentNumber}|${seqIdNo}`;
}

function copyButtonStyle(disabled, copied) {
    return {
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 500,
        color: '#fff',
        backgroundColor: copied ? '#83c2f0' : (disabled ? '#5a5a5a' : '#0182C5'),
        border: 'none',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
    };
}


export default function SeqIdAlignmentPopup({ html, data, patentNumber }) {
    const tooltipClasses = useTooltipStyles();
    const [seqIdNo, setSeqIdNo] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [copiedWhich, setCopiedWhich] = useState(null);
    const [rawSeq, setRawSeq] = useState(null); // null | 'loading' | 'error' | 'not-found' | string (plain residues)
    const [seqExpanded, setSeqExpanded] = useState(false);
    const closeTimer = useRef(null);
    const copyResetTimer = useRef(null);

    const cancelClose = useCallback(() => {
        clearTimeout(closeTimer.current);
    }, []);

    const scheduleClose = useCallback(() => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            setSeqIdNo(null);
            setAnchorEl(null);
            setCopiedWhich(null);
            setSeqExpanded(false);
        }, CLOSE_DELAY_MS);
    }, []);

    const handleMouseMove = useCallback((event) => {
        const target = event.target?.closest?.('.seq-id-no[data-seqid]');
        if (!target) return;
        cancelClose();
        const nextSeqId = target.getAttribute('data-seqid');
        if (target !== anchorEl) setAnchorEl(target);
        if (nextSeqId !== seqIdNo) {
            setSeqIdNo(nextSeqId);
            setCopiedWhich(null);
            setSeqExpanded(false);
        }
    }, [anchorEl, seqIdNo, cancelClose]);

    const copyToClipboard = (which, text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedWhich(which);
        clearTimeout(copyResetTimer.current);
        copyResetTimer.current = setTimeout(() => setCopiedWhich(null), COPY_FEEDBACK_MS);
    };

    const matchedDoc = seqIdNo ? findDocForSeqId(data, patentNumber, seqIdNo) : null;
    const alignmentHtml = matchedDoc
        ? (data?.gqAlignments?.[matchedDoc.dbkey] || data?.gqAlignmentsNoSumm?.[matchedDoc.dbkey])
        : null;

    // No local match — fetch just the disclosed sequence's raw residues from the source
    // patent database (no alignment, since no query was ever compared against it).
    useEffect(() => {
        if (matchedDoc || !seqIdNo || !patentNumber) {
            setRawSeq(null);
            return;
        }
        const key = fetchCacheKey(patentNumber, seqIdNo);
        if (_rawSeqCache.has(key)) {
            console.log('[SeqIdPopup] using cached raw sequence for', key);
            setRawSeq(_rawSeqCache.get(key));
            return;
        }

        let cancelled = false;
        setRawSeq('loading');
        console.log('[SeqIdPopup] fetching raw disclosed sequence for', { patentNumber, seqIdNo });
        DiscoverService.getDisclosedSequenceBySeqId(patentNumber, seqIdNo).then(response => {
            const seq = parseSequenceListingHtml(response?.response_content);
            console.log('[SeqIdPopup] raw sequence parsed:', seq ? `${seq.length} residues` : 'none');
            const result = seq || 'not-found';
            _rawSeqCache.set(key, result);
            if (!cancelled) setRawSeq(result);
        }).catch((err) => {
            console.error('[SeqIdPopup] raw sequence fetch rejected:', err);
            if (!cancelled) setRawSeq('error');
        });

        return () => { cancelled = true; };
    }, [matchedDoc, seqIdNo, patentNumber]);

    const rawSeqOk = typeof rawSeq === 'string' && !['loading', 'error', 'not-found'].includes(rawSeq);
    const sequences = matchedDoc
        ? parseAlignmentSequences(matchedDoc.RESULT_FALI_FORMATTED)
        : (rawSeqOk ? { query: null, subject: rawSeq } : null);

    const isLongSeq = rawSeqOk && rawSeq.length > SEQ_PREVIEW_LENGTH;
    const displaySeq = (isLongSeq && !seqExpanded) ? rawSeq.slice(0, SEQ_PREVIEW_LENGTH) : rawSeq;
    const displayBlocks = formatSequenceBlocks(displaySeq) + ((isLongSeq && !seqExpanded) ? '\n...' : '');

    const tooltipContent = !seqIdNo ? '' : (
        <div
            style={{ maxWidth: 600, fontSize: 12, color: '#fff' }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
        >
            {!matchedDoc && rawSeq === 'loading' &&
                <div>
                    <style>{`
                        @keyframes seqIdPopupSpin {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
                        <span style={{
                            width: 15,
                            height: 15,
                            borderRadius: '50%',
                            border: '2px solid rgba(231, 242, 247, 0.71)',
                            borderTopColor: '#4fc3f7',
                            display: 'inline-block',
                            animation: 'seqIdPopupSpin 0.8s linear infinite',
                        }} />
                        <span>Loading sequence for SEQ ID NO {seqIdNo}...</span>
                    </div>
                </div>
            }
            {!matchedDoc && rawSeq === 'error' &&
                <div>Failed to load disclosed sequence for SEQ ID NO {seqIdNo}.</div>
            }
            {!matchedDoc && (rawSeq === 'not-found' || rawSeq === null) &&
                <div>No sequence data available for SEQ ID NO {seqIdNo}.</div>
            }
            {matchedDoc &&
                <div>
                    <div style={{ marginBottom: 4 }}>
                        <div><b>Query:</b> {matchedDoc.QUERY_ID || 'N/A'}</div>
                        <div><b>Subject:</b> {matchedDoc.SUBJECT_ID || matchedDoc.SUBJECT_N || 'N/A'} (SEQ ID NO: {seqIdNo})</div>
                    </div>
                    {alignmentHtml &&
                        <pre
                            style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', margin: '4px 0', fontSize: 11, color: '#fff' }}
                            dangerouslySetInnerHTML={{ __html: alignmentHtml }}
                        />
                    }
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button
                            type="button"
                            style={copyButtonStyle(!sequences?.query, copiedWhich === 'query')}
                            disabled={!sequences?.query}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyToClipboard('query', sequences?.query); }}
                        >
                            {copiedWhich === 'query' ? 'Copied!' : 'Copy Query'}
                        </button>
                        <button
                            type="button"
                            style={copyButtonStyle(!sequences?.subject, copiedWhich === 'subject')}
                            disabled={!sequences?.subject}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyToClipboard('subject', sequences?.subject); }}
                        >
                            {copiedWhich === 'subject' ? 'Copied!' : 'Copy Subject'}
                        </button>
                    </div>
                </div>
            }
            {!matchedDoc && rawSeqOk &&
                <div>
                    <div style={{ marginBottom: 4 }}>
                        <div>{patentNumber}-{seqIdNo}</div>
                    </div>
                    <pre style={{ whiteSpace: 'pre', maxHeight: 240, overflow: 'auto', margin: '4px 0', fontSize: 11, color: '#fff', fontFamily: 'monospace' }}>
                        {displayBlocks}
                    </pre>
                    {isLongSeq &&
                        <span
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSeqExpanded(!seqExpanded); }}
                            style={{ color: '#4fc3f7', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            {seqExpanded ? 'less...' : 'more...'}
                        </span>
                    }
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button
                            type="button"
                            style={copyButtonStyle(false, copiedWhich === 'subject')}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyToClipboard('subject', rawSeq); }}
                        >
                            {copiedWhich === 'subject' ? 'Copied!' : 'Copy Subject'}
                        </button>
                    </div>
                </div>
            }
        </div>
    );

    return (
        <Tooltip
            arrow
            interactive
            classes={tooltipClasses}
            title={tooltipContent}
            placement="bottom"
            open={Boolean(seqIdNo) && Boolean(anchorEl)}
            PopperProps={{ anchorEl }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
        >
            <span
                onMouseMove={handleMouseMove}
                onMouseLeave={scheduleClose}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </Tooltip>
    );
}
